import { PAGINADOS } from './tokens';
import { useT } from '@/lib/i18n/useT';

/**
 * Lo que decide el RECORRIDO, encima de los pasos.
 *
 * Idiomas y paginado vivían en el panel de propiedades del carril, repartidos
 * entre dos pestañas distintas (Look y Form). Y no son propiedades de la
 * apariencia: son las dos preguntas que uno se hace mirando la lista de pasos
 * —en qué idiomas se responde esto y cómo se le dice a quien responde por
 * dónde va—. Puestas donde se hacen esas preguntas, se contestan sin abrir un
 * panel ni cambiar de pestaña.
 *
 * Van juntas porque las dos hablan del recorrido, no del color.
 */
export function JourneyTopControls({
  idiomas,
  locales,
  onLocales,
  defaultLocale = '',
  onDefaultLocale,
  paginado,
  onPaginado,
}: {
  /** Los que declara la ENCUESTA. Con uno solo no hay nada que elegir. */
  idiomas: string[];
  /** Los que este evento ofrece. Vacío = todos los de la encuesta. */
  locales: string[];
  onLocales: (locales: string[]) => void;
  /**
   * Con cuál ARRANCA la encuesta en este evento. `''` = el de la encuesta.
   * Es columna del despliegue (`defaultLocale`), no un override.
   */
  defaultLocale?: string;
  onDefaultLocale?: (locale: string) => void;
  /** `''` = el que traiga la encuesta. */
  paginado: string;
  onPaginado: (v: string) => void;
}) {
  const t = useT();
  const activos = locales.length > 0 ? locales : idiomas;

  return (
    <div className="flex flex-col gap-3">
      {/*
        Con un solo idioma no se pinta: un selector de una opción no es un
        selector, y ocuparía sitio para decir algo que ya se sabe.
      */}
      {idiomas.length > 1 ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-fg-muted)]">
            {t('designJourney.locales')}
          </span>
          <div className="flex flex-wrap gap-2">
            {idiomas.map((l) => {
              const marcado = activos.includes(l);
              /*
               * No se deja quitar el último.
               *
               * Sin idiomas, el runtime no tiene en cuál pintar la encuesta y
               * quien escanea el QR se encuentra una pantalla en blanco. Se
               * apaga el botón en vez de dejar hacerlo y avisar después.
               */
              const ultimo = marcado && activos.length === 1;
              return (
                <button
                  key={l}
                  type="button"
                  disabled={ultimo}
                  onClick={() =>
                    onLocales(marcado ? activos.filter((x) => x !== l) : [...activos, l])
                  }
                  title={ultimo ? t('designJourney.localesLast') : undefined}
                  className={
                    'rounded-md px-2 py-1 text-[11px] font-semibold uppercase transition-colors disabled:cursor-not-allowed ' +
                    (marcado
                      ? 'bg-[var(--color-accent)] text-[var(--color-fg-on-accent)]'
                      : 'bg-[var(--color-bg-input)] text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]')
                  }
                >
                  {l.toUpperCase()}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
            {t('designJourney.localesHint')}
          </p>
          {/*
            Con cuál se abre. Sólo entre los que se ofrecen: arrancar en un
            idioma que este evento no enseña dejaría al visitante sin poder
            volver a él después de cambiar.
          */}
          {onDefaultLocale && activos.length > 1 ? (
            <div className="mt-1 flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-fg-muted)]">
                {t('designJourney.defaultLocale')}
              </span>
              <div role="radiogroup" aria-label={t('designJourney.defaultLocale')} className="flex flex-wrap gap-2">
                {['', ...activos].map((l) => {
                  const marcado = (defaultLocale || '') === l;
                  return (
                    <button
                      key={l || '__survey'}
                      type="button"
                      role="radio"
                      aria-checked={marcado}
                      onClick={() => onDefaultLocale(l)}
                      className={
                        'rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ' +
                        (marcado
                          ? 'bg-[var(--color-accent)] text-[var(--color-fg-on-accent)]'
                          : 'bg-[var(--color-bg-input)] text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]')
                      }
                    >
                      {l ? l.toUpperCase() : t('designJourney.fromSurvey')}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
                {t('designJourney.defaultLocaleHint')}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      <label className="flex flex-col gap-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-fg-muted)]">
          {t('designJourney.progress')}
        </span>
        <select
          value={paginado}
          onChange={(e) => onPaginado(e.target.value)}
          className="h-8 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-2 text-[12px] text-[var(--color-fg-primary)] focus:border-[var(--color-accent-ink)] focus:outline-none"
        >
          <option value="">{t('designJourney.fromSurvey')}</option>
          {PAGINADOS.map((p) => (
            <option key={p} value={p}>
              {t(`designJourney.paginado.${p}`)}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
